import * as qs from 'query-string';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { exactVariantForOptions } from '../../../lib/product';
import * as types from '../../../types';

interface ProductDetailsQueryParams {
  color?: types.ColorOption;
  size?: types.SizeOption;
  type?: types.CollarTypeOption;
}

function useDefaultVariantOptions(variants: types.IVariant[], useQueryParams: boolean): types.IVariantOptions {
  const location = useLocation();

  const { initialCollarType, initialColor, initialSize } = useMemo(() => {
    const queryParams: ProductDetailsQueryParams = useQueryParams ? qs.parse(location.search) : {};
    return {
      initialCollarType: queryParams.type,
      initialColor: queryParams.color,
      initialSize: queryParams.size,
    };
  }, [location.search, useQueryParams]);

  return useMemo(() => {
    // `variants` is empty when the catalog doesn't carry the product here (Pick your Fi without S3+), so fall
    // back to the usual defaults rather than reading a first variant
    const backupOptions = variants[0]?.options;

    const preferredTypes = [initialCollarType, types.CollarTypeOption.Standard];
    const availablePreferredType = preferredTypes.find((t) => variants.some((v) => v.options.collarType === t));
    const collarType = availablePreferredType ?? backupOptions?.collarType ?? types.CollarTypeOption.Standard;

    const preferredColors = [initialColor, types.ColorOption.Yellow].filter((colorOption) => !!colorOption);
    const availablePreferredColor = preferredColors.find((c) => variants.some((v) => v.options.color === c));
    const color = availablePreferredColor ?? backupOptions?.color ?? types.ColorOption.Yellow;

    const preferredSizes = [initialSize, types.SizeOption.Medium];
    const availablePreferredSize = preferredSizes.find((s) => variants.some((v) => v.options.size === s));
    const size = availablePreferredSize ?? backupOptions?.size ?? types.SizeOption.Medium;

    return {
      collarType,
      color,
      size,
    };
  }, [initialCollarType, initialColor, initialSize, variants]);
}

interface VariantSelectorProps {
  onColorChange: (newVariant: types.IVariant) => void;
  onSelectedVariantChange: (newVariant: types.IVariant) => void;
  onSizeChange: (newVariant: types.IVariant) => void;
  variants: types.IVariant[];
  useQueryParams: boolean;
}

export default function useVariantSelector({
  onColorChange,
  onSelectedVariantChange,
  onSizeChange,
  variants,
  useQueryParams,
}: VariantSelectorProps) {
  const history = useHistory();
  const defaultOptions = useDefaultVariantOptions(variants, useQueryParams);
  const [selectedVariant, setSelectedVariant] = useState<types.IVariant>(
    exactVariantForOptions(variants, defaultOptions) ?? variants[0],
  );
  const location = useLocation();
  const queryParams: ProductDetailsQueryParams = useMemo(
    () => (useQueryParams ? qs.parse(location.search) : {}),
    [useQueryParams, location.search],
  );
  const [hasUpdatedSize, setHasUpdatedSize] = useState<boolean>(!!queryParams.size);

  const changeVariantOptions = useCallback(
    (o: Partial<types.IVariantOptions>) => {
      const newOptions = { ...selectedVariant?.options, ...o };
      const newVariant = exactVariantForOptions(variants, newOptions) ?? variants[0];
      if (!newVariant) {
        return;
      }

      if (!o.color && o.size) {
        setHasUpdatedSize(true);
      }

      if (newVariant.options.color !== selectedVariant?.options.color) {
        onColorChange(newVariant);
      }

      if (newVariant.options.size !== selectedVariant?.options.size) {
        onSizeChange(newVariant);
      }

      setSelectedVariant(newVariant);

      if (useQueryParams) {
        const updatedQueryParams = { ...queryParams, ...newOptions };
        const queryString = qs.stringify(updatedQueryParams);
        history.replace({ search: queryString, state: { noScroll: true } });
      }
    },
    [selectedVariant?.options, variants, useQueryParams, onColorChange, onSizeChange, queryParams, history],
  );

  useEffect(() => {
    onSelectedVariantChange(selectedVariant);
  }, [onSelectedVariantChange, selectedVariant]);

  /**
   * If we no longer have a valid variant, switch into new default options. This is useful where the selected
   * options might be for S3 options, but then toggled to S2 that do not have the same variants (e.g. scribble-out
   * color or XS size). This will keep any applicable options, but choose new defaults for the ones that are not
   * applicable.
   */
  useEffect(() => {
    if (variants.length > 0 && !variants.find((variant) => variant.sku === selectedVariant?.sku)) {
      changeVariantOptions(defaultOptions);
    }
  }, [changeVariantOptions, defaultOptions, selectedVariant, variants]);

  return {
    changeVariantOptions,
    selectedVariant,
    hasUpdatedSize,
  };
}
